import { useInkind } from '@gik/api/inkinds/inkind';
import ContactPageOrganizersForAddress, {
  DOORDASH_TIMELINE,
  GRUBHUB_TIMELINE,
  ServiceModule,
  isGiftCardServiceModule,
  isPartnerServiceModule,
  usePartnerTimeline,
} from '@gik/calendar';
import { AddToCalendarButton } from '@gik/calendar/components/AddToCalendarSelector/AddToCalendarButton';
import DoorDashButton from '@gik/calendar/components/DoorDashButton/DoorDashButton';
import GrubhubButton from '@gik/calendar/components/GrubhubButton/GrubhubButton';
import { CalendarEventTypeIds } from '@gik/calendar/models/CalendarEventTypes';
import type { IUserProfileClaim } from '@gik/calendar/models/Claim';
import { getEndDateTime, getStartDateTime } from '@gik/calendar/models/Claim';
import DateTimeUtils, { isInPastAllTimezones } from '@gik/core/utils/DateTimeUtils';
import bemBlock from '@gik/core/utils/bemBlock';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import i18n from '@gik/i18n';
import isInkindPageRecipientInfoComplete from '@gik/inkind-page/utils/isInkindPageRecipientInfoComplete';
import { PageAvatar } from '@gik/ui-legacy/Avatar';
import { CopyAddressButton } from '@gik/ui-legacy/CopyButton';
import { SvgIcon } from '@gik/ui-legacy/SvgIcon';
import { Text } from '@gik/ui-legacy/Text/Text';
import { UI } from '@gik/ui-legacy/UIManager';
import ChevronRightIcon from '@heroicons/react/outline/ChevronRightIcon';
import moment from 'moment';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { GiftCardClaimSection } from '../GiftCardClaimSection/GiftCardClaimSection';
import { translationKeys } from './i18n/en';

export interface ClaimedItemModalProps {
  claimedItem: IUserProfileClaim;
  getFormattedTime: () => JSX.Element;
  dateFormat: string;
}

const contentBlockName = 'claimed-item-modal-content';

function ClaimedItemModalComp({ claimedItem, getFormattedTime, dateFormat }: ClaimedItemModalProps): JSX.Element {
  const bem = bemBlock(contentBlockName);
  const { t } = useTranslation();

  const { data: inkindPage } = useInkind(claimedItem.inkindRouteId);

  const isPastClaim = React.useMemo(() => isInPastAllTimezones(getStartDateTime(claimedItem)), [claimedItem]);

  // TODO: manage doordash claims in a separate component, and if possible reuse code from the claim modal success component

  const isPartner = isPartnerServiceModule(claimedItem.serviceModule);
  const isGiftCard = isGiftCardServiceModule(claimedItem.serviceModule);
  const isProvideMyself = claimedItem.serviceModule === ServiceModule.PROVIDE_MYSELF;
  const timeline = claimedItem.serviceModule === ServiceModule.DOORDASH ? DOORDASH_TIMELINE : GRUBHUB_TIMELINE;
  const { canOrderNow, canOrderFrom } = usePartnerTimeline(timeline, getStartDateTime(claimedItem));
  const serviceModuleName = claimedItem.serviceModule === ServiceModule.DOORDASH ? 'DoorDash' : 'Grubhub';

  return (
    <div className={bem('pages')}>
      <section className={bem('who')}>
        <div className={bem('section-title')}>{t(translationKeys.who)}</div>

        <a href={claimedItem.pageUrl} className={bem('who-container')}>
          <PageAvatar
            className={bem('page-image')}
            imageUrl={claimedItem.pageThumbnail}
            rounded
            circle={false}
            size={'lg'}
          />
          <span className={bem('page-title')}>{claimedItem.pageTitle}</span>

          <div className={bem('who-more-info-icon')}>
            <SvgIcon Icon={ChevronRightIcon} />
          </div>
        </a>

        {!isGiftCard && !isPastClaim && (
          <div className={bem('address-container')}>
            <div className={bem('who-name')}>{claimedItem.recipientAddressName}</div>
            {inkindPage && !isInkindPageRecipientInfoComplete(inkindPage) ? (
              <ContactPageOrganizersForAddress inkindRouteId={claimedItem.inkindRouteId} variant="centered" />
            ) : (
              <>
                <div className={bem('who-address')}>{claimedItem.recipientAddress}</div>
                <CopyAddressButton textToCopy={claimedItem.recipientAddress} />
              </>
            )}
          </div>
        )}

        {isGiftCard && <GiftCardClaimSection claimedItem={claimedItem} />}

        {isPastClaim && (
          <div className={bem('address-container')}>
            <div className={bem('address-not-displayed')}>{t(translationKeys.addressNotDisplayed)}</div>
          </div>
        )}
      </section>
      {!isGiftCard && (
        <section className={bem('when')}>
          <div className={bem('section-title')}>{t(translationKeys.when)}</div>

          <div className={bem('date-time-container')}>
            <div className={bem('date')}>{getStartDateTime(claimedItem).format(dateFormat)}</div>
            <div className={bem('time')}>{getFormattedTime()}</div>
          </div>

          {!isPastClaim && !canOrderNow && claimedItem.addToCalendarLinks && (
            <div>
              <AddToCalendarButton links={claimedItem.addToCalendarLinks} />
            </div>
          )}
        </section>
      )}

      {/* what section */}
      <section className={bem('what')}>
        <div className={bem('section-title')}>{t(translationKeys.what)}</div>

        <div className={bem('claim-description')}>
          {claimedItem.entry.typeName}
          {claimedItem.entry.numberRequired && ` for ${claimedItem.entry.numberRequired}`}
          {isPartner && t(translationKeys.deliveryService)}
        </div>

        {isProvideMyself && (
          <>
            {claimedItem.entry.description && (
              <>
                <div className={bem('entry-label')}>{t(translationKeys.description)}</div>
                <div className={bem('entry-text')}>
                  <Text parseUrls>{claimedItem.entry.description}</Text>
                </div>
              </>
            )}
            {claimedItem.entry.deliveryInstructions && (
              <>
                <div className={bem('entry-label')}>{t(translationKeys.deliveryInstructions)}</div>
                <div className={bem('entry-text')}>
                  <Text parseUrls>{claimedItem.entry.deliveryInstructions}</Text>
                </div>
              </>
            )}
            {claimedItem.entry.pickupLocation && (
              <>
                <div className={bem('entry-label')}>{t(translationKeys.pickupLocation)}</div>
                <div className={bem('entry-text')}>
                  <Text parseUrls>{claimedItem.entry.pickupLocation}</Text>
                </div>
              </>
            )}
            {claimedItem.entry.dropoffLocation && (
              <>
                <div className={bem('entry-label')}>{t(translationKeys.dropoffLocation)}</div>
                <div className={bem('entry-text')}>
                  <Text parseUrls>{claimedItem.entry.dropoffLocation}</Text>
                </div>
              </>
            )}
          </>
        )}

        {!isProvideMyself && (
          <>
            <div className={bem('entry-title')}>{claimedItem.entry.title}</div>
            <p className={bem('entry-description')}>
              {claimedItem.entry.description ? (
                <Text parseUrls>{claimedItem.entry.description}</Text>
              ) : (
                t(translationKeys.noDescription)
              )}
            </p>
          </>
        )}

        {!isPastClaim && isPartner && (
          <div className={bem('partner')}>
            {canOrderNow ? (
              <p>{t(translationKeys.toSchedule)}</p>
            ) : (
              <>
                <p>
                  {t(translationKeys.youWillBeAble)}
                  <span>
                    {moment.utc(canOrderFrom).format('MMMM Do')},{' '}
                    {moment.utc(canOrderFrom).format('YYYY').toUpperCase()}
                  </span>
                  .
                </p>
                <p>
                  {t(translationKeys.seeWhatsAvailable, {
                    cityAndState:
                      claimedItem.recipientCityAndState.indexOf(', NA') < 0
                        ? `${claimedItem.recipientCityAndState}, so`
                        : '',
                  })}
                </p>
              </>
            )}
            {claimedItem.serviceModule === ServiceModule.DOORDASH && (
              <DoorDashButton url={claimedItem.partnerUrl} canOrderNow={canOrderNow} />
            )}
            {claimedItem.serviceModule === ServiceModule.GRUBHUB && (
              <GrubhubButton url={claimedItem.partnerUrl} canOrderNow={canOrderNow} />
            )}
            {canOrderNow && (
              <p>
                {t(translationKeys.beSureToOrder)}
                <br />
                <span>{getStartDateTime(claimedItem).format('MMMM Do')}</span>
                {!claimedItem.entry.allDay && (
                  <span>
                    , &nbsp;
                    {getStartDateTime(claimedItem).format(DateTimeUtils.Time12HourFormat)}
                    &nbsp;-&nbsp;
                    {getEndDateTime(claimedItem).format(DateTimeUtils.Time12HourFormat)}
                  </span>
                )}
              </p>
            )}
            {canOrderNow && (
              <p className={bem('copy-reminder')}>{t(translationKeys.pleaseRememberToCopy, serviceModuleName)}</p>
            )}
          </div>
        )}
      </section>

      {/* your notes section */}
      {isProvideMyself && (claimedItem.carType || claimedItem.description || claimedItem.note) && (
        <section className={bem('notes-section')}>
          <div className={bem('notes')}>{t(translationKeys.yourNotes)}</div>

          {claimedItem.carType && (
            <>
              <div className={bem('claim-label')}>{t(translationKeys.carType)}</div>
              <div className={bem('claim-text')}>{<Text parseUrls>{claimedItem.carType}</Text>}</div>
            </>
          )}
          {claimedItem.description && (
            <>
              <div className={bem('claim-label')}>
                {/* same condition like in Helpers\CalendarLinkGenerator.cs */}
                {claimedItem.entry.typeEventCategory === 'meal'
                  ? t(translationKeys.bringing).toString()
                  : claimedItem.entry.typeId === CalendarEventTypeIds.Other ||
                      claimedItem.entry.typeId === CalendarEventTypeIds.Groceries
                    ? t(translationKeys.providing).toString()
                    : t(translationKeys.contactInformation).toString()}
              </div>
              <div className={bem('claim-text')}>{<Text parseUrls>{claimedItem.description}</Text>}</div>
            </>
          )}
          {claimedItem.note && (
            <>
              <div className={bem('claim-label')}>{t(translationKeys.personalNote).toString()}</div>
              <div className={bem('claim-text')}>{<Text parseUrls>{claimedItem.note}</Text>}</div>
            </>
          )}
        </section>
      )}

      {!isProvideMyself && (claimedItem.note || claimedItem.description) && (
        <section className={bem('notes-section')}>
          <div className={bem('notes')}>{t(translationKeys.yourNotes).toString()}</div>

          <div className={bem('claim-description')}>{<Text parseUrls>{claimedItem.description}</Text>}</div>
          <div className={bem('claim-notes')}>{<Text parseUrls>{claimedItem.note}</Text>}</div>
        </section>
      )}

      {!isGiftCard && (
        <section className={bem('special-notes-section')}>
          <div className={bem('special-notes-title')}>{t(translationKeys.specialNotes).toString()}</div>

          <p>
            {claimedItem.pageNotes ? (
              <Text parseUrls>{claimedItem.pageNotes}</Text>
            ) : (
              t(translationKeys.noSpecialNotes).toString()
            )}
          </p>
        </section>
      )}
    </div>
  );
}

export const ClaimedItemModal = withComponentErrorBoundary(ClaimedItemModalComp);

const modalBlockName = 'claimed-item-modal';
const bem = bemBlock(modalBlockName);

export const openClaimedItemModal = (props: ClaimedItemModalProps): void => {
  UI.dialog(<ClaimedItemModal {...props} />, {
    className: bem(),
    title: i18n.t(translationKeys.claimInfo),
    closable: true,
    modalProps: { shouldCloseOnOverlayClick: false },
  });
};
